import { translationKey } from '../i18n';
import { localize } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import Dialog from '../components/StableDialog';
import useRequestGuard from '../hooks/useRequestGuard';
import PageLoading from '../components/PageLoading';
import ConnectionRetryState from '../components/ConnectionRetryState';
import LoadingLabel from '../components/LoadingLabel';
import React, { useState, useEffect } from 'react';

import { Box, Typography, Fab, LinearProgress, CircularProgress, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseIcon from '@mui/icons-material/Pause';
import MedicationIcon from '@mui/icons-material/Medication';
import MedImage from '../components/MedImage';
import AppToast from '../components/AppToast';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PageHeader from '../components/PageHeader';
import ScrollDownIndicator from '../components/ScrollDownIndicator';
import BottomSheet from '../components/BottomSheet';
import { getDeviceUuid, getMedications, deleteMedication, updateMedication } from '../api';
import { syncWidget } from '../utils/widgetSync';
import useMedicationAddGate from '../hooks/useMedicationAddGate';
import { isMedicationPaused, isPauseActive } from '../utils/medicationPause';
import { clearMedicationCaches } from '../utils/localCache';
import AppButton from '../components/ui/AppButton';
import AppCard from '../components/ui/AppCard';
import useAppLanguage from '../i18n';

const FREQUENCY_LABEL = {
    DAILY: translationKey('매일'), EVERY_OTHER_DAY: translationKey('격일'), SPECIFIC_DAYS: translationKey('특정 요일'),
    HOURLY_INTERVAL: translationKey('몇 시간마다'), EVERY_N_DAYS: translationKey('며칠마다'), CYCLE: translationKey('먹고 쉬고 반복'), MONTHLY: translationKey('매월 특정일'),
    AS_NEEDED: translationKey('필요할 때만'),
};

const DAY_LABEL = {
    MON: translationKey('월'), TUE: translationKey('화'), WED: translationKey('수'), THU: translationKey('목'), FRI: translationKey('금'), SAT: translationKey('토'), SUN: translationKey('일'),
};
const DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const SECTION_GAP = 2;
const CARD_GAP = 1.15;
const FINISHED_PAGE_SIZE = 10;
const SECTION_LABEL_SX = {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#888',
};
const SECTION_TITLE_SX = {
    minHeight: 32,
    display: 'flex',
    alignItems: 'center',
    mb: 0.5,
    ...SECTION_LABEL_SX,
};

function localDateKey() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function finishedOn(med, today) {
    return Boolean(med.endDate && med.endDate < today);
}

function startsLater(med, today) {
    return Boolean(med.startDate && med.startDate > today);
}

function dateLabel(date) {
    const [year, month, day] = date.split('-');
    return `${year}.${month}.${day}`;
}

function endDateLabel(date, t) {
    return t('{date}까지 복용', { date: dateLabel(date) });
}

function dayRank(day) {
    const idx = DAY_ORDER.indexOf(day);
    return idx === -1 ? DAY_ORDER.length : idx;
}

function frequencyLabel(med, t) {
    if (med.frequencyType !== 'SPECIFIC_DAYS') {
        return t(FREQUENCY_LABEL[med.frequencyType] || med.frequencyType);
    }

    const days = Array.isArray(med.specificDays)
        ? med.specificDays
        : (med.specificDays || '').split(',');
    const labels = days
        .map(day => day.trim())
        .filter(Boolean)
        .sort((a, b) => dayRank(a) - dayRank(b))
        .map(day => t(DAY_LABEL[day] || day));

    return labels.length > 0
        ? `${t(FREQUENCY_LABEL.SPECIFIC_DAYS)} (${labels.join(', ')})`
        : t(FREQUENCY_LABEL.SPECIFIC_DAYS);
}

const triggerHaptic = (style = 'light') => {
    if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'HAPTIC', style }));
    }
};

export default function MedicationListPage() {
    const { language, t } = useAppLanguage();
    const navigate = useNavigate();
    const { openMedicationAdd, MedicationAddGateDialog, checkingMedicationLimit } = useMedicationAddGate();
    const cachedMeds = React.useMemo(() => {
        try { const r = sessionStorage.getItem('medlist_cache'); return r ? JSON.parse(r) : null; } catch { return null; }
    }, []);
    const [medications, setMedications] = useState(cachedMeds || []);
    const [loading, setLoading] = useState(!cachedMeds);
    const [menuMed, setMenuMed] = useState(null);
    // BottomSheet open을 별도 state로 분리 — menuMed 데이터를 Dialog transition 동안 유지하면서
    // 시트 노출만 명시적으로 끄기 위함 (취소 시 시트가 잠깐 다시 열리는 깜빡임 방지).
    const [menuSheetOpen, setMenuSheetOpen] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [photoMed, setPhotoMed] = useState(null);
    const [photoOpen, setPhotoOpen] = useState(false);
    const [photoError, setPhotoError] = useState(null);
    const [today, setToday] = useState(localDateKey);
    const [visibleFinishedCount, setVisibleFinishedCount] = useState(FINISHED_PAGE_SIZE);

    const [error, setError] = useState(false);

    const beginRequest = useRequestGuard('medlist_cache');
    const fetchMedications = async () => {
        const isCurrent = beginRequest();
        try {
            setError(false);
            const uuid = getDeviceUuid();
            const meds = await getMedications(uuid);
            if (!isCurrent()) return;
            setMedications(meds);
            try { sessionStorage.setItem('medlist_cache', JSON.stringify(meds)); } catch {}
        } catch { if (isCurrent()) setError(true); } finally { if (isCurrent()) setLoading(false); }
    };

    useEffect(() => {
        fetchMedications();
        let lastRefresh = Date.now();
        const refreshVisible = () => {
            if (document.visibilityState === 'hidden' || Date.now() - lastRefresh < 1000) return;
            lastRefresh = Date.now();
            fetchMedications();
        };
        window.addEventListener('focus', refreshVisible);
        document.addEventListener('visibilitychange', refreshVisible);
        return () => {
            window.removeEventListener('focus', refreshVisible);
            document.removeEventListener('visibilitychange', refreshVisible);
        };
    }, []);
    useEffect(() => {
        if (!photoOpen || !photoMed) return;
        const current = medications.find(med => med.id === photoMed.id);
        if (!current?.imageUrl) setPhotoOpen(false);
        else if (current.imageUrl !== photoMed.imageUrl || current.name !== photoMed.name) {
            setPhotoMed(current);
            setPhotoError(null);
        }
    }, [medications, photoOpen, photoMed]);
    useEffect(() => {
        const timer = setInterval(() => setToday(localDateKey()), 60 * 1000);
        return () => clearInterval(timer);
    }, []);

    const handleDeleteClick = () => {
        // 시트 먼저 닫고 Dialog 열기 — menuMed 데이터는 유지 (Dialog title에 표시 위해)
        setMenuSheetOpen(false);
        setDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!menuMed || processing) return;
        const med = menuMed;
        // menuMed는 Dialog onExited에서 정리 — transition 중 title 빈 따옴표 방지
        setProcessing(true);
        setDeletingId(med.id);
        try {
            const uuid = getDeviceUuid();
            await deleteMedication(uuid, med.id);
            clearMedicationCaches();
            triggerHaptic('light');
            setSnackbar({ open: true, message: t('삭제 완료'), severity: 'success' });
            await fetchMedications();
            setDeleteConfirm(false);
            syncWidget(uuid);
        } catch { setSnackbar({ open: true, message: t('삭제 실패'), severity: 'error' }); }
        finally { setProcessing(false); setDeletingId(null); }
    };

    const handleTogglePause = async () => {
        if (!menuMed || processing) return;
        setProcessing(true);
        try {
            const uuid = getDeviceUuid();
            await updateMedication(uuid, menuMed.id, { paused: !menuMed.paused });
            clearMedicationCaches();
            triggerHaptic('light');
            setSnackbar({ open: true, message: t(menuMed.paused ? '복용을 재개했어요' : '복용을 중지했어요'), severity: 'success' });
            setMenuSheetOpen(false);
            fetchMedications();
            syncWidget(uuid);
        } catch { setSnackbar({ open: true, message: t('오류 발생'), severity: 'error' }); }
        finally { setProcessing(false); }
    };

    if (loading) {
        return <PageLoading />;
    }

    const currentMeds = medications.filter(med => !finishedOn(med, today));
    const activeMeds = currentMeds.filter(med => !isMedicationPaused(med));
    const pausedMeds = currentMeds.filter(med => isMedicationPaused(med)).sort((a, b) => a.id - b.id);
    const finishedMeds = medications.filter(med => finishedOn(med, today))
        .sort((a, b) => b.endDate.localeCompare(a.endDate) || b.id - a.id);

    return (
        <Box sx={{ pb: 10 }}>
            <PageHeader title={t('약 목록')} subtitle={currentMeds.length > 0 ? t('{count}개의 약을 관리하고 있어요', { count: currentMeds.length }) : undefined} />

            <Box sx={{ px: 2, pt: 2 }}>
                {error && medications.length === 0 ? (
                    <ConnectionRetryState onRetry={() => { setLoading(true); fetchMedications(); }} />
                ) : medications.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <MedicationIcon sx={{ fontSize: 48, mb: 2, color: '#8a7000' }} />
                        <SingleLineText sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#1a1a1a', mb: 0.5 }}>{t('등록된 약이 없어요')}</SingleLineText>
                        <SingleLineText sx={{ color: '#777', fontSize: '1rem' }}>{t('아래 + 버튼으로 약을 등록해보세요')}</SingleLineText>
                    </Box>
                ) : (() => {
                    const getTimeLabel = (time) => {
                        const h = parseInt(time.split(':')[0], 10);
                        if (h >= 5 && h < 11) return t('아침');
                        if (h >= 11 && h < 15) return t('점심');
                        if (h >= 15 && h < 21) return t('저녁');
                        return t('취침 전');
                    };

                    const scheduledMeds = activeMeds.filter(m => m.frequencyType !== 'AS_NEEDED').sort((a, b) => {
                        const aFirst = (a.times || []).map(t => t.slice(0, 5)).sort()[0] || '99:99';
                        const bFirst = (b.times || []).map(t => t.slice(0, 5)).sort()[0] || '99:99';
                        return aFirst.localeCompare(bFirst) || a.id - b.id;
                    });
                    const asNeededMeds = activeMeds.filter(m => m.frequencyType === 'AS_NEEDED').sort((a, b) => a.id - b.id);

	                    const renderCard = (med) => {
	                        const isFinished = finishedOn(med, today);
	                        const isUpcoming = !isFinished && startsLater(med, today);
	                        const isPaused = !isFinished && isMedicationPaused(med);
	                        const stockPercent = (med.totalStock && med.remainingStock != null)
	                            ? (med.remainingStock / med.totalStock) * 100 : null;
	                        const times = (med.times || []).map(t => t.slice(0, 5)).sort();
	                        const timeRows = times.reduce((rows, time, idx) => {
	                            const rowIndex = Math.floor(idx / 3);
	                            if (!rows[rowIndex]) rows[rowIndex] = [];
	                            rows[rowIndex].push(time);
	                            return rows;
	                        }, []);
	                        const isAsNeeded = med.frequencyType === 'AS_NEEDED';

                        return (
                            <AppCard
                                key={med.id}
                                data-medication-id={med.id}
                                sx={{
                                    p: 1.6,
                                    opacity: deletingId === med.id || isPaused ? 0.55 : 1,
                                    transition: 'opacity 0.3s ease',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box component="button" type="button"
                                        aria-label={med.imageUrl ? localize("{v0} 사진 크게 보기", { v0: med.name }) : localize("{v0} 수정", { v0: med.name })}
                                        onClick={() => {
                                            if (!med.imageUrl) { navigate(`/medications/${med.id}/edit`); return; }
                                            setPhotoMed(med);
                                            setPhotoError(null);
                                            setPhotoOpen(true);
                                        }}
                                        sx={{ p: 0, border: 0, borderRadius: 2, bgcolor: 'transparent', flexShrink: 0, cursor: 'pointer', '&:active': { opacity: 0.6 } }}>
                                        <MedImage src={med.imageUrl} size={48} />
                                    </Box>
                                    <Box component="button" type="button" sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, minHeight: 48,
                                        p: 0, border: 0, bgcolor: 'transparent', textAlign: 'left',
                                        cursor: 'pointer', '&:active': { opacity: 0.6 },
                                        '&:focus-visible': { outline: '2px solid #8a7000', outlineOffset: 2 },
                                    }} onClick={() => navigate(`/medications/${med.id}/edit`)}>
	                                        <Box sx={{ flex: 1, minWidth: 0 }}>
	                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.7 }}>
	                                                <Typography sx={{
		                                                    fontWeight: 700, fontSize: '1.08rem', color: '#1a1a1a',
	                                                    lineHeight: 1.35,
	                                                    overflow: 'hidden',
	                                                    display: '-webkit-box',
	                                                    WebkitLineClamp: 2,
	                                                    WebkitBoxOrient: 'vertical',
	                                                    overflowWrap: 'anywhere',
	                                                    wordBreak: 'keep-all',
	                                                    flex: 1,
	                                                }}>{med.name}</Typography>
	                                                {(isFinished || isPaused || isUpcoming) && (
	                                                    <Box sx={{ px: 0.75, py: 0.12, borderRadius: 1.2, bgcolor: isFinished ? '#f1f5f9' : (isPaused ? '#fef3c7' : '#fff9e6'), flexShrink: 0, mt: 0.08 }}>
		                                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: isFinished ? '#64748b' : (isPaused ? '#d97706' : '#8a7000') }}>
		                                                            {t(isFinished ? '복용 완료' : (isPaused ? '복용 중지' : '복용 예정'))}
		                                                        </Typography>
	                                                    </Box>
	                                                )}
	                                            </Box>
                                            <Typography sx={{ fontSize: '0.98rem', color: '#777', mt: 0.25, fontWeight: 500 }}>
                                                {frequencyLabel(med, t)}
                                            </Typography>
                                            {isFinished && (
                                                <Typography sx={{ fontSize: '0.86rem', color: '#999', mt: 0.15 }}>
                                                    {endDateLabel(med.endDate, t)}
                                                </Typography>
                                            )}
                                            {isUpcoming && (
                                                <Typography sx={{ fontSize: '0.86rem', color: '#8a7000', mt: 0.15 }}>
                                                    {t('{date}부터 복용', { date: dateLabel(med.startDate) })}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box component="button" type="button" aria-label={localize("{v0} 더보기", { v0: med.name })}
                                        onClick={() => { setMenuMed(med); setMenuSheetOpen(true); }}
                                        sx={{
                                            p: 0,
                                            ml: -0.4,
                                            color: '#aaa',
                                            flexShrink: 0,
                                            width: 48,
                                            height: 48,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: 0,
                                            bgcolor: 'transparent',
                                            cursor: 'pointer',
                                            borderRadius: 2.5,
                                            '&:focus-visible': { outline: '2px solid #8a7000', outlineOffset: 2 },
                                            '&:active': { opacity: 0.6, bgcolor: '#f8fafc' },
                                        }}>
                                        <MoreVertIcon sx={{ fontSize: 22 }} />
                                    </Box>
                                </Box>

	                                {!isFinished && !isAsNeeded && timeRows.length > 0 && (
	                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.15 }}>
	                                        <AccessTimeIcon sx={{ fontSize: 15, color: '#bbb', flexShrink: 0, mt: 0.18 }} />
	                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 0 }}>
	                                            {timeRows.map((row, rowIndex) => (
	                                                <Box key={`time-row-${rowIndex}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.45, flexWrap: 'nowrap', minHeight: 20 }}>
	                                                    {row.map((time, idx) => (
	                                                        <Box key={time} sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
	                                                            {idx > 0 && <Typography sx={{ fontSize: '0.9rem', color: '#ddd' }}>·</Typography>}
		                                                            <Typography sx={{ fontSize: '0.94rem', fontWeight: 600, color: '#555' }}>
	                                                                {time}
	                                                            </Typography>
		                                                            <Typography sx={{ fontSize: '0.82rem', color: '#aaa', fontWeight: 500 }}>
	                                                                {getTimeLabel(time)}
	                                                            </Typography>
	                                                        </Box>
	                                                    ))}
	                                                </Box>
	                                            ))}
	                                        </Box>
	                                    </Box>
	                                )}

	                                {med.memo?.trim() && (
	                                    <Typography
	                                        sx={{
	                                            mt: 1.05,
	                                            px: 1.2,
	                                            py: 0.9,
	                                            borderRadius: 2,
	                                            bgcolor: '#fafafa',
	                                            color: '#666',
	                                            fontSize: '0.92rem',
	                                            lineHeight: 1.45,
	                                            display: '-webkit-box',
	                                            WebkitLineClamp: 2,
	                                            WebkitBoxOrient: 'vertical',
	                                            overflow: 'hidden',
	                                        }}
	                                    >
	                                        {med.memo}
	                                    </Typography>
	                                )}

                                {!isFinished && stockPercent !== null && (
                                    <Box component="button" type="button" aria-label={localize("{v0} 재고 관리", { v0: med.name })}
                                        sx={{ mt: 1.15, p: 0, width: '100%', minHeight: 48, border: 0, bgcolor: 'transparent', display: 'flex', alignItems: 'center', gap: 1.4, cursor: 'pointer',
                                            '&:focus-visible': { outline: '2px solid #8a7000', outlineOffset: 2 } }}
                                        onClick={() => navigate('/settings/stock')}>
                                        <Box sx={{ flex: 1 }}>
                                            <LinearProgress variant="determinate" value={stockPercent}
                                                sx={{
                                                    height: 6, borderRadius: 3, bgcolor: '#f0f0f0',
                                                    '& .MuiLinearProgress-bar': { bgcolor: stockPercent < 20 ? '#ef4444' : '#ffe566', borderRadius: 3 },
                                                }} />
                                        </Box>
                                        <Typography sx={{ color: stockPercent < 20 ? '#ef4444' : '#777', fontWeight: 600, fontSize: '1.05rem', whiteSpace: 'nowrap' }}>
                                            {language === 'ko' ? localize("{v0}/{v1}정", { v0: med.remainingStock, v1: med.totalStock }) : t('{remaining}/{total} doses', { remaining: med.remainingStock, total: med.totalStock })}
                                        </Typography>
                                    </Box>
                                )}
                            </AppCard>
                        );
                    };

                    return (
                        <>
                            {currentMeds.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                                    <CheckCircleOutlineIcon sx={{ fontSize: 44, color: '#d6bc48', mb: 1 }} />
                                    <SingleLineText sx={{ fontWeight: 700, fontSize: '1.16rem', color: '#1a1a1a', mb: 0.5 }}>{t('지금 복용 중인 약이 없어요')}</SingleLineText>
                                    <SingleLineText sx={{ color: '#777', fontSize: '0.96rem' }}>{t('복용을 마친 약은 아래에서 확인할 수 있어요')}</SingleLineText>
                                </Box>
                            )}
                            {scheduledMeds.length > 0 && (
                                <Box>
                                    {asNeededMeds.length > 0 && (
                                        <SingleLineText sx={SECTION_TITLE_SX}>
                                            {t('정해진 시간에 먹는 약')}
                                        </SingleLineText>
                                    )}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: CARD_GAP }}>
                                        {scheduledMeds.map(renderCard)}
                                    </Box>
                                </Box>
                            )}
                            {asNeededMeds.length > 0 && (
                                <Box sx={{ mt: scheduledMeds.length > 0 ? SECTION_GAP : 0 }}>
                                    <SingleLineText sx={SECTION_TITLE_SX}>
                                        {t('필요할 때 먹는 약')}
                                    </SingleLineText>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: CARD_GAP }}>
                                        {asNeededMeds.map(renderCard)}
                                    </Box>
                                </Box>
                            )}
                            {pausedMeds.length > 0 && (
                                <Box component="section" aria-labelledby="paused-medications-title" sx={{ mt: activeMeds.length > 0 ? SECTION_GAP : 0 }}>
                                    <SingleLineText id="paused-medications-title" sx={SECTION_TITLE_SX}>{t('복용 중지한 약')}</SingleLineText>
                                    <Box id="paused-medications" sx={{ display: 'flex', flexDirection: 'column', gap: CARD_GAP }}>
                                        {pausedMeds.map(renderCard)}
                                    </Box>
                                </Box>
                            )}
                            {finishedMeds.length > 0 && (
                                <Box sx={{ mt: currentMeds.length > 0 ? SECTION_GAP : 0 }}>
                                    <SingleLineText sx={SECTION_TITLE_SX}>{t('복용 완료한 약')}</SingleLineText>
                                    <Box id="finished-medications" sx={{ display: 'flex', flexDirection: 'column', gap: CARD_GAP }}>
                                        {finishedMeds.slice(0, visibleFinishedCount).map(renderCard)}
                                    </Box>
                                    {finishedMeds.length > visibleFinishedCount && (
                                        <AppButton tone="secondary" fullWidth
                                            onClick={() => setVisibleFinishedCount(count => count + FINISHED_PAGE_SIZE)}
                                            sx={{ mt: CARD_GAP, fontSize: '0.9rem', fontWeight: 600 }}>
                                            {t('{count}개 더 보기', { count: Math.min(FINISHED_PAGE_SIZE, finishedMeds.length - visibleFinishedCount) })}
                                        </AppButton>
                                    )}
                                </Box>
                            )}
                        </>
                    );
                })()}
            </Box>

            <Dialog open={photoOpen} onClose={() => setPhotoOpen(false)}
                aria-labelledby="medication-photo-title" maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: 3, mx: 2, width: 'calc(100% - 32px)' } }}>
                <DialogTitle id="medication-photo-title" sx={{ fontSize: '1.15rem', fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {photoMed?.name}
                </DialogTitle>
                <DialogContent sx={{ px: 1.5, pb: 1.5 }}>
                    {photoError && photoError === photoMed?.imageUrl ? (
                        <SingleLineText role="status" sx={{ textAlign: 'center', py: 5, color: '#777' }}>{t('사진을 불러오지 못했어요')}</SingleLineText>
                    ) : photoMed && (
                        <Box component="img" key={photoMed.imageUrl} src={photoMed.imageUrl} alt={localize("{v0} 사진", { v0: photoMed.name })}
                            onError={() => setPhotoError(photoMed.imageUrl)}
                            sx={{ display: 'block', width: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: 2, bgcolor: '#f8fafc' }} />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 1.5, pt: 0 }}>
                    <AppButton fullWidth onClick={() => setPhotoOpen(false)} sx={{ borderRadius: 2.5 }}>
                        {t('닫기')}
                    </AppButton>
                </DialogActions>
            </Dialog>

            {/* FAB */}
            <Fab disabled={checkingMedicationLimit} variant="extended" onClick={() => openMedicationAdd('/medications/new-bundle?mode=manual')}
                sx={{
                    position: 'fixed', bottom: 80,
                    right: 'calc(50% - min(240px, 50vw) + 20px)',
                    bgcolor: '#ffe566', color: '#5a4a00',
                    '&:hover': { bgcolor: '#e8d040' },
                    boxShadow: '0 4px 12px rgba(230,168,0,0.4)',
                    fontWeight: 700, fontSize: '0.95rem', gap: 0.5,
                    borderRadius: '28px', px: 2.5,
                }}>
                <AddIcon sx={{ fontSize: 20 }} />
                {checkingMedicationLimit ? <LoadingLabel /> : t('약 추가')}
            </Fab>

            {/* Menu BottomSheet — open state를 menuMed와 분리해 transition 중 데이터 유지 */}
            <BottomSheet open={menuSheetOpen} onClose={() => setMenuSheetOpen(false)}>
                {menuMed && (
                    <>
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a', mb: 2 }}>{menuMed.name}</Typography>
                        <Box role="button" tabIndex={0} onClick={() => { setMenuSheetOpen(false); navigate(`/medications/${menuMed.id}/edit`); }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.8, cursor: 'pointer', '&:active': { opacity: 0.6 } }}>
                            <EditIcon sx={{ color: '#8a7000' }} />
                            <SingleLineText sx={{ fontSize: '1.05rem', color: '#1a1a1a' }}>{t('수정하기')}</SingleLineText>
                        </Box>
                        {!finishedOn(menuMed, today) && <Box role="button" tabIndex={0} onClick={processing ? undefined : () => {
                                if (isPauseActive(menuMed.globalPausedUntil)) {
                                    setMenuSheetOpen(false);
                                    setSnackbar({ open: true, message: t('전체 복용 중지는 설정에서 해제해주세요'), severity: 'info' });
                                    return;
                                }
                                handleTogglePause();
                            }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.8, cursor: 'pointer', opacity: processing ? 0.4 : 1, '&:active': { opacity: processing ? 0.4 : 0.6 } }}>
                            {processing ? <CircularProgress size={22} /> : menuMed.paused ? <PlayArrowIcon sx={{ color: '#22c55e' }} /> : <PauseIcon sx={{ color: '#f59e0b' }} />}
                            <Typography sx={{ fontSize: '1.05rem', color: '#1a1a1a' }}>
                                {t(isPauseActive(menuMed.globalPausedUntil) ? '전체 복용 중지 중' : menuMed.paused ? '복용 재개하기' : '복용 중지하기')}
                            </Typography>
                        </Box>}
                        <Box role="button" tabIndex={0} onClick={processing ? undefined : handleDeleteClick}
                            sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.8, cursor: 'pointer', opacity: processing ? 0.4 : 1, '&:active': { opacity: processing ? 0.4 : 0.6 } }}>
                            <DeleteIcon sx={{ color: '#ef4444' }} />
                            <SingleLineText sx={{ fontSize: '1.05rem', color: '#ef4444' }}>{t('삭제')}</SingleLineText>
                        </Box>
                    </>
                )}
            </BottomSheet>

            {/* Delete confirmation dialog.
                menuMed는 Dialog가 fade-out 완전히 끝난 뒤에 정리 — 그렇지 않으면
                transition 중 타이틀이 "'' 약을..."로 빈 따옴표가 깜빡임. */}
            <Dialog open={deleteConfirm} onClose={() => { if (!processing) setDeleteConfirm(false); }}
                TransitionProps={{ onExited: () => setMenuMed(null) }}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3, px: 1 } }}>
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, pb: 0.5 }}>
                    {t("'{name}' 약을 삭제할까요?", { name: menuMed?.name || '' })}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: '1.05rem', color: '#777' }}>
                        {t('더 이상 알림이 오지 않아요. 이미 복용한 기록은 유지돼요.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
                    <Button disabled={processing} onClick={() => setDeleteConfirm(false)} variant="outlined"
                        sx={{ color: '#666', borderColor: '#e5e7eb', fontWeight: 600, fontSize: '1.05rem', borderRadius: 2, flex: 1 }}>
                        {t('아니요')}
                    </Button>
                    <Button disabled={processing} onClick={handleDeleteConfirm} variant="contained"
                        sx={{ bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '1.05rem', borderRadius: 2, flex: 1, '&:hover': { bgcolor: '#dc2626' } }}>
                        {processing ? <LoadingLabel /> : t('삭제할게요')}
                    </Button>
                </DialogActions>
            </Dialog>

            <AppToast open={snackbar.open} autoHideDuration={snackbar.severity === 'error' ? 4000 : 3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
            <ScrollDownIndicator />
            <MedicationAddGateDialog />
        </Box>
    );
}
